<template>
  <section class="access-view">
    <header class="access-view__header">
      <h1 class="access-view__title">
        {{ $t('AccessView.label.header.title') }}
      </h1>

      <DsButton
        class="access-view__button"
        @click="openAccessModal"
      >
        {{ $t('AccessView.label.header.button') }}
      </DsButton>
    </header>

    <p class="access-view__subtitle">
      {{ $t('AccessView.label.subtitle') }}
    </p>

    <div class="access-view__search">
      <v-text-field
        v-model="search"
        dense
        light
        outlined
        prepend-inner-icon="mdi-magnify"
        color="var(--ds-color-icon-2)"
        :placeholder="$t('AccessView.label.input.placeholder')"
      />
    </div>

    <v-data-table
      v-if="success"
      light
      :headers="headers"
      :items="accesses"
      :items-per-page="10"
      :search="search"
      :loading-text="$t('AccessView.notification.loadingData')"
      :loading="loading"
    >
      <template v-slot:[`item.createdDate`]="{ item }">
        <span>{{ getFormattedDate(item) }}</span>
      </template>
      <template v-slot:[`item.isStaging`]="{ item }">
        <span>{{ getLabel(item) }}</span>
      </template>
      <template v-slot:[`item.users`]="{ item }">
        <span>{{ item.firstAccess }}</span>
      </template>
      <template v-slot:[`item.actions`]="{ item }">
        <DsButton
          icon
          name="edit"
          color="ghost"
          @click="setAccessToEdit(item)"
        />

        <v-tooltip
          top
          color="var(--ds-color-reverse)"
          :disabled="!disableRemoveButton(item)"
        >
          <template v-slot:activator="{ on, attrs }">
            <div
              class="d-inline-block"
              v-bind="attrs"
              v-on="on"
            >
              <v-btn
                icon
                light
                color="grey darken-1"
                :disabled="disableRemoveButton(item)"
                @click="removeAccess(item)"
              >
                <v-icon light>
                  mdi-trash-can-outline
                </v-icon>
              </v-btn>
            </div>
          </template>
          <span>Exclusão desabilitada</span>
        </v-tooltip>
      </template>
    </v-data-table>

    <AccessModal
      v-if="formDashModal"
      v-model="formDashModal"
      :dashboard="accessToEdit"
      :modal-title="$t('AccessView.label.modal.title')"
    />

    <DsModal
      :open="accessDialog"
      :title="$t('AccessView.dialog.title')"
      :text="$t('AccessView.dialog.text')"
      :primary-button-label="$t('AccessView.dialog.primaryButtonLabel')"
      :secondary-button-label="$t('AccessView.dialog.secondaryButtonLabel')"
      :width="'600px'"
      @secondary="cancelAction()"
      @primary="confirmAction()"
    />
  </section>
</template>

<script>
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex';
import requestStatusMixin from '@/mixins/request-status.mixin';
import AccessModal from '@/components/AccessModal';
import { MUTATE } from '@/store/modules/mutation_types';
import { DsButton, DsModal } from '@ds/components';
import firebase from '@/plugins/firebase';

export default {
  name: 'AccessView',
  components: {
    DsButton,
    AccessModal,
    DsModal,
  },
  mixins: [
    requestStatusMixin,
  ],
  data() {
    return {
      search: '',
      accessToEdit: null,
      accessDialog: false,
      currentDashboadData: {},
      requestStatus: 'loading',
      headers: [
        {
          text: this.$t('AccessView.label.table.accountId'),
          value: 'accountId',
        },
        {
          text: this.$t('AccessView.label.table.universeId'),
          value: 'universeId',
        },
        {
          text: this.$t('AccessView.label.table.createdDate'),
          value: 'createdDate',
        },
        {
          text: this.$t('AccessView.label.table.environment'),
          value: 'isStaging',
        },
        {
          text: this.$t('AccessView.label.table.email'),
          value: 'users',
        },
        {
          text: this.$t('AccessView.label.table.onboarding'),
          value: 'onboardingStatus.value',
        },
        {
          text: '',
          value: 'actions',
          sortable: false
        },
      ],
      tempFilter: {},
    };
  },
  computed: {
    ...mapState('dashboard', [
      'universes'
    ]),
    ...mapState('misc', [
      'accessModal',
    ]),
    ...mapState('user', [
      'user'
    ]),
    ...mapGetters('dashboard', [
      'dashboards'
    ]),
    formDashModal: {
      get() {
        return this.accessModal;
      },
      set(newValue) {
        this.mutateMisc({
          property: 'accessModal',
          with: newValue,
        });
      },
    },
    accesses() {
      const rowAccesses = this.dashboards?.filter(({ createdByAccessPage }) => createdByAccessPage === true) || [];
      const accesses = rowAccesses.map((dashboard) => {
        return {
          ...dashboard,
          firstAccess: dashboard.users.filter(({ admin }) => admin)[0]?.email || '---',
        };
      });

      return accesses;
    },
  },
  async created() {
    await this.getAccessDashboards(this.tempFilter);

    this.requestStatus = 'success';
  },
  methods: {
    ...mapActions('dashboard', [
      'getAccessDashboards',
      'editPermission',
    ]),
    ...mapMutations('misc', {
      mutateMisc: MUTATE
    }),
    openAccessModal() {
      this.mutateMisc({
        property: 'accessModal',
        with: true,
      });
      this.accessToEdit = null;
    },
    setAccessToEdit(access) {
      this.mutateMisc({
        property: 'accessModal',
        with: true,
      });

      this.accessToEdit = access;
    },
    getFormattedDate(item) {
      if (!item.audit?.createdDate) return '---';

      const [year, month, day] = item.audit.createdDate?.toDate().toISOString().substr(0, 10).split('-');
      return `${day}/${month}/${year}`;
    },
    getLabel(item) {
      return item.isStaging ? this.$t('AccessView.label.table.homologEnv') : this.$t('AccessView.label.table.prodEnv');
    },
    disableRemoveButton(item) {
      return item?.onboardingStatus?.key !== 'TO_DO';
    },
    removeAccess(item) {
      this.currentDashboadData = { ...item };

      this.accessDialog = true;
    },
    cancelAction() {
      this.accessDialog = false;
    },
    async confirmAction() {
      const newUsers = this.currentDashboadData.users?.filter(({ admin }) => !admin) || [];

      await this.editPermission({
        universeId: this.currentDashboadData.universeId,
        dashboardId: this.currentDashboadData.dashboardId,
        users: newUsers,
        userEmails: newUsers.map(({ email }) => email),
        expiresTrialDate: Boolean(this.currentDashboadData?.expiresTrialDate) ? firebase.firestore.Timestamp.fromDate(new Date(this.expiresTrialDate)) : null,
        audit: {
          updatedBy: this.user.name,
          updatedByEmail: this.user.email,
          updatedDate: firebase.firestore.Timestamp.fromDate(new Date()),
        }
      });

      this.currentDashboadData = {};
      this.newFirstAccess = '';

      this.$noty.success(
        this.$t('AccessView.notification.removeAccess'),
        { layout: 'bottomCenter' });

      this.accessDialog = false;
    },
  },
};
</script>

<style scoped lang="scss">
  .access-view {
    padding: 24px 85px 24px 38px;
    width: 100%;
    color: var(--default-text-color);

    &__header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: var(--ds-spacing-stack-nano);
    }

    &__title {
      color: var(--ds-color-text-1);
      font-weight: var(--ds-font-weight-semibold);
      font-size: var(--ds-font-size-xl);
      line-height: 48px;
    }

    &__button {
      max-height: 40px;
    }

    &__subtitle {
      font: var(--ds-font-family-base);
    }

    &__search {
      margin-bottom: var(--ds-spacing-stack-sm);
      max-width: 356px;
    }
  }

  ::v-deep {
    .ds-button--ghost .ds-icon {
      background-color: var(--ds-color-icon-2);
      cursor: pointer;
    }

    .v-btn::before {
      background-color: transparent;
    }
  }
</style>
